'use client';

// ProcessingIndicator - Animated loading state for bot creation
// Shows Boty mascot with pulse animation, progress bar, and reassurance text

import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import { useTranslations } from 'next-intl';
import { BotyIcon } from '@boty/ui';

export interface ProcessingIndicatorProps {
  /** Optional custom message (translation key or string) */
  message?: string;
  /** Estimated duration in ms (default 30000) */
  estimatedDuration?: number;
  /** Whether processing is complete */
  isComplete?: boolean;
  /** Callback when fade-out animation completes */
  onTransitionComplete?: () => void;
}

/**
 * Calculate progress based on elapsed time with non-linear curve
 * Fast start, slow middle, very slow end to avoid "stuck" appearance
 */
function calculateProgress(elapsed: number, estimatedDuration: number): number {
  const ratio = Math.min(elapsed / estimatedDuration, 1);

  if (ratio < 0.33) {
    // Fast start: 0-50% in first third of time
    return ratio * 3 * 50; // 0 → 50%
  } else if (ratio < 0.66) {
    // Slow middle: 50-90% in second third
    return 50 + (ratio - 0.33) * 3 * 40; // 50 → 90%
  } else {
    // Very slow end: 90-99% in last third (never reaches 100 until isComplete)
    return 90 + (ratio - 0.66) * 3 * 9; // 90 → 99%
  }
}

/**
 * ProcessingIndicator - Animated loading state with Boty mascot
 *
 * Displays an engaging processing screen with:
 * - Animated Boty mascot with pulse animation
 * - "Creating your bot..." headline
 * - Progress bar with non-linear timing
 * - "This takes about 30 seconds" reassurance text
 *
 * The progress bar uses a non-linear curve that:
 * - Starts fast (0-50% in first third)
 * - Slows in middle (50-90% in second third)
 * - Very slow at end (90-99% in final third)
 * - Only reaches 100% when isComplete is true
 *
 * @example
 * ```tsx
 * <ProcessingIndicator
 *   estimatedDuration={30000}
 *   isComplete={processingDone}
 *   onTransitionComplete={() => navigateToNext()}
 * />
 * ```
 */
export function ProcessingIndicator({
  message,
  estimatedDuration = 30000,
  isComplete = false,
  onTransitionComplete,
}: ProcessingIndicatorProps) {
  const t = useTranslations('creator.processing');
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(true);

  // Handle transition complete callback
  const handleTransitionEnd = useCallback(() => {
    if (!visible && onTransitionComplete) {
      onTransitionComplete();
    }
  }, [visible, onTransitionComplete]);

  // Progress animation logic
  useEffect(() => {
    if (isComplete) {
      setProgress(100);
      // Start fade-out after progress reaches 100%
      const fadeTimeout = setTimeout(() => {
        setVisible(false);
      }, 100);
      return () => clearTimeout(fadeTimeout);
    }

    const startTime = Date.now();
    let animationFrameId: number;

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const newProgress = calculateProgress(elapsed, estimatedDuration);
      setProgress(newProgress);

      if (newProgress < 99) {
        animationFrameId = requestAnimationFrame(updateProgress);
      }
    };

    animationFrameId = requestAnimationFrame(updateProgress);
    return () => cancelAnimationFrame(animationFrameId);
  }, [estimatedDuration, isComplete]);

  // Handle fade transition completion
  useEffect(() => {
    if (!visible) {
      const timeout = setTimeout(handleTransitionEnd, 300);
      return () => clearTimeout(timeout);
    }
  }, [visible, handleTransitionEnd]);

  return (
    <Fade in={visible} timeout={300}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 300, sm: 400 },
          textAlign: 'center',
          p: { xs: 2, sm: 4 },
          width: '100%',
        }}
        role="status"
        aria-live="polite"
      >
        {/* Animated Boty mascot */}
        <Box
          sx={{
            mb: { xs: 3, sm: 4 },
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(1.05)', opacity: 0.9 },
            },
          }}
        >
          <BotyIcon size={120} variant="full" />
        </Box>

        {/* Headline */}
        <Typography
          variant="h5"
          fontWeight="bold"
          gutterBottom
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          {message || t('title')}
        </Typography>

        {/* Progress bar */}
        <Box sx={{ width: '100%', maxWidth: 400, mb: 2, px: { xs: 2, sm: 0 } }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'grey.200',
              // Flip progress direction in RTL
              transform: (theme) =>
                theme.direction === 'rtl' ? 'scaleX(-1)' : 'none',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main', // Indigo #4f46e5
                borderRadius: 4,
              },
            }}
          />
        </Box>

        {/* Reassurance text */}
        <Typography variant="body2" color="text.secondary">
          {t('reassurance')}
        </Typography>
      </Box>
    </Fade>
  );
}
